<template>
  <div class="flex h-full flex-col justify-between space-y-6">
    <div class="h-full">
      <div :class="plus_invest ? 'mb-3' : ''" class="flex items-center justify-center space-x-2">
        <template v-if="item.strategy_market !== 'Equities'">
          <p class="text-ontick-steel-500 text-center text-lg font-semibold dark:text-gray-300">
            {{ typed_volume }}
          </p>
          <h2 class="text-ontick-steel-500 text-center text-lg font-semibold dark:font-medium dark:text-gray-300">
            {{ volumeUnit }}
          </h2>
        </template>
        <div v-else class="flex items-center space-x-2">
          <h2
            class="text-ontick-steel-500 text-center text-lg font-semibold dark:font-medium dark:text-gray-300"
            :content="isFinancialVolume ? 'Volume financeiro aproximado' : 'Quantidade de ações'"
            v-tippy="{ arrow: true, theme: 'green' }"
          >
            {{ isFinancialVolume ? "Financeiro" : "Quantidade" }}
          </h2>
          <button
            content="Alternar entre quantidade de ações e volume financeiro"
            v-tippy="{ arrow: true, theme: 'green' }"
            @click="changeMode"
            v-if="!DEV_FIXED_VOLUME_TYPE"
          >
            <i v-html="$icons.swap"></i>
            <span class="sr-only"> Alternar entre quantidade de ações e volume financeiro </span>
          </button>
        </div>
      </div>
      <div
        class="flex items-center justify-center whitespace-nowrap rounded-md shadow-sm"
        :class="item.strategy_market === 'Equities' ? ' pt-4' : 'h-full'"
      >
        <div class="relative mb-6 flex-1" v-if="item.strategy_market === 'Futures'">
          <div is="transition" v-on:enter="enter" v-on:leave="leave" v-bind:css="false" appear mode="out-in">
            <vue-slider
              :key="1"
              v-if="!plus_invest"
              :railStyle="{ backgroundColor: disabled ? '#6c737f' : '#046299' }"
              :min="item.min_volume"
              :max="item.max_scale_volume"
              :included="true"
              :process="false"
              :marks="marks"
              hideLabel
              tooltip="none"
              class="mb-3"
              :tooltip-placement="['bottom']"
              v-model="typed_volume"
              @change="$emit('seletedParam', typed_volume)"
              @drag-end="$emit('statusValidate', false)"
              :disabled="disabled"
            >
              <template v-slot:tooltip="{ value }">
                <div
                  class="bg-ontick-blue-900 -mt-1.5 rounded-full bg-opacity-75 px-6 py-1 text-center text-xs font-semibold text-gray-200"
                >
                  {{ value }}
                </div>
              </template>
              <template v-slot:step="{ active, value }">
                <div
                  class="-translate-x-1/2 transform pt-3 text-[10px]"
                  :class="active ? 'text-ontick-blue-600' : 'dark:text-gray-300'"
                >
                  <span>
                    {{ value }}
                  </span>
                </div>
              </template>
            </vue-slider>

            <card-edit-input-component
              v-else
              :key="2"
              :contract="item"
              :typed_volume_value="typed_volume"
              @inputValidate="$emit('statusValidate', $event)"
              @updateValue="(typed_volume = $event), $emit('seletedParam', typed_volume)"
            />
          </div>
        </div>
        <div v-else-if="item.strategy_market === 'Equities'" class="flex items-center space-x-2">
          <ontick-number-input-component
            class="w-full"
            size="full"
            v-model="typed_volume"
            @input="$emit('seletedParam', typed_volume)"
            :min="item.min_volume"
            :max="item.max_absolute_volume"
            :step="isFinancialVolume ? 1000 : item.volume_step"
            :currency="isFinancialVolume"
            :disabled="disabled"
          />
        </div>
        <div
          v-if="item.strategy_market !== 'Equities'"
          is="transition"
          v-on:enter="enter"
          v-on:leave="leave"
          v-bind:css="false"
          appear
          mode="out-in"
        >
          <span
            @click="(plus_invest = !plus_invest), (typed_volume = !plus_invest ? item.min_volume : item.volume_step)"
            v-show="active_button"
            class="text-ontick-blue-600 cursor-pointer pl-3 dark:text-gray-200"
            :class="[activeButton(), plus_invest ? 'mt-1.5' : '-mt-1']"
            v-html="!plus_invest ? $icons.plus : $icons.close"
          ></span>
        </div>
      </div>
    </div>
    <div class="flex justify-between">
      <div
        :content="item.limit_type === 'PER_TRADE' ? 'Perda por operação' : 'Perda máxima diária'"
        v-tippy="{ arrow: true, theme: 'green' }"
        v-if="item.strategy_market === 'Equities'"
      >
        <p class="label_card truncate" v-if="item.limit_type === 'PER_TRADE'">Perda/op.</p>
        <p class="label_card truncate" v-else-if="item.limit_type === 'DAILY'">Perda máx.</p>

        <p v-if="volumeIsLoading" class="flex w-full items-center justify-center">
          <i v-html="$icons.loading" class="text-ontick-blue-600 scale-90 transform"></i>
          <span class="sr-only">Carregando</span>
        </p>
        <p v-else class="text-ontick-red-600 dark:text-ontick-red-500 text-left text-sm font-medium">
          {{ maxLoss | currency }}
        </p>
      </div>
      <div v-else content="Perda máxima diária" v-tippy="{ arrow: true, theme: 'green' }">
        <p class="label_card truncate">Perda máx.</p>
        <p class="text-ontick-red-600 dark:text-ontick-red-500 text-left text-sm font-medium">
          {{ maxLoss | currency }}
        </p>
      </div>

      <div :content="capitalOrVolume.tooltip" v-tippy="{ arrow: true, theme: 'green' }">
        <p class="label_card truncate text-center">
          {{ capitalOrVolume.title }}
        </p>
        <template v-if="item.strategy_market === 'Equities'">
          <p v-if="volumeIsLoading" class="flex w-full items-center justify-center">
            <i v-html="$icons.loading" class="text-ontick-blue-600 scale-90 transform"></i>
            <span class="sr-only">Carregando</span>
          </p>
          <template v-else>
            <p
              class="text-ontick-steel-500 text-center text-sm font-semibold dark:text-gray-300"
              v-if="isFinancialVolume"
            >
              {{ item.qtd_volume }}
            </p>
            <p
              class="dark:font-regular text-ontick-steel-500 text-center text-sm font-semibold dark:text-gray-300"
              v-else
            >
              {{ item.fin_volume | currency }}
            </p>
          </template>
        </template>
        <p class="dark:font-regular text-ontick-steel-500 text-center text-sm font-semibold dark:text-gray-300" v-else>
          {{ capitalOrVolume.value | currency }}
        </p>
      </div>
      <div
        v-if="item.strategy_market === 'Equities'"
        :content="item.limit_type === 'PER_TRADE' ? 'Ganho por operação' : 'Ganho máximo diário'"
        v-tippy="{ arrow: true, theme: 'green' }"
      >
        <p class="label_card truncate" v-if="item.limit_type === 'PER_TRADE'">Ganho/op.</p>
        <p class="label_card truncate" v-else-if="item.limit_type === 'DAILY'">Ganho máx.</p>
        <p v-if="volumeIsLoading" class="flex w-full items-center justify-center">
          <i v-html="$icons.loading" class="text-ontick-blue-600 scale-90 transform"></i>
          <span class="sr-only">Carregando</span>
        </p>
        <p v-else class="text-ontick-green-700 dark:text-ontick-green-500 text-center text-sm font-semibold">
          {{ maxGain | currency }}
        </p>
      </div>
      <div v-else content="Ganho máximo diário" v-tippy="{ arrow: true, theme: 'green' }">
        <p class="label_card truncate">Ganho máx.</p>

        <p class="text-ontick-green-700 dark:text-ontick-green-500 text-center text-sm font-semibold">
          {{ maxGain | currency }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import CardEditInputComponent from "./card.edit.input.component.vue";
import * as icons from "@/app/assets/icons/investments";
import OntickNumberInputComponent from "../../../../../../../shared/components/basic/ontick-number-input.component.vue";
import OntickSwitchComponent from "../../../../../../../shared/components/basic/ontick-switch.component.vue";

export default {
  props: {
    item: {
      type: Object,
    },
    disabled: {
      type: Boolean,
    },
  },
  components: {
    VueSlider,
    CardEditInputComponent,
    OntickNumberInputComponent,
    OntickSwitchComponent,
  },

  data() {
    return {
      marks: (val) =>
        val % this.item.volume_step === 0
          ? {
              label: val,
              labelStyle: {
                opacity: 1,
              },
            }
          : false,

      typed_volume: 0,
      plus_invest: false,
      active_button: false,

      optionDefault: {
        icon: icons.details,
        label: "",
      },
      optionsSelect: [
        { icon: icons.editParams, label: "Parâmetros" },
        { icon: icons.performance, label: "Histórico" },
        {
          icon: icons.removeInvestment,
          label: this.investmentActiveOrHidden == "ACTIVE" ? "Arquivar" : "Desarquivar",
        },
      ],
      financialVolume: this.item.financialVolume,
      quantityVolume: this.item.quantityVolume,
      volumeIsLoading: true,
      tempVolume: 0,
      tempQuantity: 0,
      shareQuantity: 1,
      sharePrice: 27.48,
      DEV_FIXED_VOLUME_TYPE: false, // [DEV] Bloqueia possibilidade de mudança de tipo de volume
      timeout: setTimeout(() => this.getVolumeCheck, 2000),
    };
  },

  computed: {
    ...mapState({
      selectedParameterInvestment: (state) => state.investment.selected_parameter,
    }),
    isFinancialVolume() {
      return this.item.volume_type === "FIN_VOLUME";
    },
    roundedQuantity() {
      let initialValue = parseInt(this.typed_volume / this.sharePrice);
      if (initialValue % 100 === 0) {
        return initialValue;
      } else {
        return initialValue - (initialValue % 100);
      }
    },
    capitalOrVolume() {
      if (this.item.strategy_market === "Equities") {
        if (this.isFinancialVolume) {
          return {
            tooltip: "Quantidade aproximada de ações",
            title: "Quantidade",
          };
        } else {
          return {
            tooltip: "Volume financeiro aproximado",
            title: "Financeiro",
          };
        }
      } else {
        return {
          tooltip: "Capital recomendado",
          title: "Capital",
          value: this.typed_volume * (this.item.min_capital / this.item.volume_step),
        };
      }
    },
    maxLoss() {
      if (this.item.strategy_market === "Equities") {
        return this.item.max_loss * -1;
      } else {
        return this.typed_volume * this.item.max_daily_loss;
      }
    },
    maxGain() {
      if (this.item.strategy_market === "Equities") {
        return this.item.max_gain;
      } else {
        return this.typed_volume * this.item.max_daily_gain;
      }
    },
    volumeUnit() {
      switch (this.item.volume_text) {
        case "contratos":
          if (this.typed_volume > 1) {
            return "Contratos";
          } else {
            return "Contrato";
          }
        default:
          return "Contratos";
      }
    },
  },

  methods: {
    ...mapActions({
      selectedParams: "investment/selectedParams",
      setInvestmentVolumeType: "investment/setInvestmentVolumeType",
      setInvestmentVolumeByType: "investment/setInvestmentVolumeByType",
      setGainLoss: "investment/setGainLoss",
    }),
    changeMode() {
      if (this.item.volume_type === "FIN_VOLUME") {
        this.setInvestmentVolumeType({
          investment_id: this.item.investment_id,
          volume_type: "QUANTITY",
        });
        this.typed_volume = this.item.qtd_volume;
      } else if (this.item.volume_type === "QUANTITY") {
        this.setInvestmentVolumeType({
          investment_id: this.item.investment_id,
          volume_type: "FIN_VOLUME",
        });
        this.typed_volume = this.item.fin_volume;
      }
    },
    enter(el, done) {
      TweenMax.fromTo(
        el,
        0.5,
        {
          autoAlpha: 0,
          scale: 1.5,
        },
        {
          autoAlpha: 1,
          scale: 1,
          transformOrigin: "50% 50%",
          ease: Power4.easeOut,
          onComplete: done,
        }
      );
    },
    leave(el, done) {
      TweenMax.fromTo(
        el,
        0.5,
        {
          autoAlpha: 1,
          scale: 1,
        },
        {
          autoAlpha: 0,
          scale: 0.8,
          ease: Power4.easeOut,
          onComplete: done,
        }
      );
    },

    activeButton() {
      let color;
      if (this.typed_volume == this.item.max_scale_volume) {
        this.active_button = true;
        color = "text-ontick-blue-600";
      } else if (this.plus_invest) {
        this.active_button = true;
        color = "text-ontick-blue-600";
      } else {
        this.active_button = false;
        color = "text-ontick-graphite-500";
      }
      return color;
    },

    colorResult(amount) {
      if (amount == 0) return "text-gray-300";
      if (amount > 0) return "text-ontick-green-600";
      if (amount < 0) return "text-ontick-red-600";
    },

    getVolumeCheck() {
      if (this.item.strategy_market === "Equities") {
        this.setGainLoss({ investment_id: this.item.investment_id })
          .catch(() => {
            // console.log("card-edit");
          })
          .finally(() => {
            this.volumeIsLoading = false;
          });
      }
    },

    volumeCheckHandler() {
      this.volumeIsLoading = true;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.getVolumeCheck(), 500);
    },
  },

  watch: {
    item: {
      handler(val) {
        this.typed_volume = val.curr_volume;
      },
    },
    "item.volume_type": {
      // immediate: true,
      handler(val) {
        if (this.item.strategy_market === "Equities") {
          this.setInvestmentVolumeType({
            investment_id: this.item.investment_id,
            volume_type: val,
          });

          this.volumeCheckHandler();
        }
      },
    },
    typed_volume: {
      handler(val) {
        this.$emit("seletedParam", val);
        if (this.item.strategy_market === "Equities") {
          this.volumeCheckHandler();
        }
      },
    },
  },

  created() {
    this.typed_volume = this.item.curr_volume;
    if (!this.active_button) {
      this.$emit("statusValidate", false);
    }
  },
};
</script>

<style lang="scss">
.label_card {
  @apply text-xs text-gray-500 dark:text-gray-400;
}
.status_card {
  @apply text-center text-xs font-semibold;
}
.investiment_input {
  @apply bg-ontick-steel-100 text-ontick-carbon-900 dark:bg-ontick-steel-500 block w-full rounded border py-1 text-center  dark:text-gray-300 sm:text-sm;
}
.error {
  @apply text-ontick-red-500 text-[11px];
}
.vue-slider-mark-label {
  @apply text-center text-[10px] text-gray-100 #{!important};
}

// .vue-slider-rail {
//   @apply bg-ontick-blue-900;
// }

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
  overflow: hidden;
  position: absolute;
  visibility: hidden;
  width: 100%;
  opacity: 0;
}

.fade-leave-to {
  // overflow: hidden;
  visibility: hidden;
  position: absolute;
  width: 100%;
  opacity: 0;
}
</style>
